import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Pencil, Trash2 } from 'lucide-react';

const ManageServicesDialog = ({
  open,
  onOpenChange,
  services,
  onCreateService,
  onUpdateService,
  onDeleteService
}) => {
  const [newService, setNewService] = useState({ name: '', description: '', defaultAmount: 0 });
  const [editService, setEditService] = useState(null);
  const [editServiceDialog, setEditServiceDialog] = useState(false);

  const handleCreate = async () => {
    const success = await onCreateService(newService);
    if (success) {
      setNewService({ name: '', description: '', defaultAmount: 0 });
    }
  };

  const handleUpdate = async () => {
    const success = await onUpdateService(editService);
    if (success) {
      setEditServiceDialog(false);
      setEditService(null);
    }
  };

  const openEditDialog = (service) => {
    setEditService({ ...service });
    setEditServiceDialog(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Services</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Existing Services List */}
            {services.length > 0 && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">Existing Services</Label>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {services.map((service) => (
                    <div key={service._id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-gray-500">{service.description || 'No description'}</p>
                        {service.defaultAmount > 0 && (
                          <p className="text-sm text-gray-500">Default: ${service.defaultAmount}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(service)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteService(service._id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Service Form */}
            <div className="border-t pt-4 space-y-4">
              <Label className="text-base font-semibold">Create New Service</Label>
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  placeholder="e.g., Tax Return Preparation"
                  value={newService.name}
                  onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Service description"
                  value={newService.description}
                  onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Amount</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={newService.defaultAmount || ''}
                  onChange={(e) => setNewService(prev => ({ ...prev, defaultAmount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreate} className="flex-1">
                  Create Service
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={editServiceDialog} onOpenChange={setEditServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Service</DialogTitle>
          </DialogHeader>
          {editService && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={editService.name}
                  onChange={(e) => setEditService(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editService.description || ''}
                  onChange={(e) => setEditService(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Default Amount</Label>
                <Input
                  type="number"
                  value={editService.defaultAmount || ''}
                  onChange={(e) => setEditService(prev => ({ ...prev, defaultAmount: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleUpdate} className="flex-1">
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditServiceDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageServicesDialog;
